import Section from '@pelckmans/elementis/components/section';
import { Cell } from '@pelckmans/elementis/components/table';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Group } from '../../../../../api/groups';
import LoadingState from '../../../../../shared-components/LoadingState';
import ModuleContext from '../../../../material/_contexts/ModuleContext';
import { splitAssignmentsByEndDateCategory, splitAssignmentsByPastEndDateCategory } from '../../utils';
import { TeacherAssignment } from '../api/AssignmentTeacherApi';
import TeacherAssignmentsContext, { TeacherAssignmentsContextProvider } from '../contexts/TeacherAssignmentsContext';
import AssignmentDivision from './AssignmentDivision';
import GroupsEmptyState from './GroupsEmptyState';
import NoAssignmentsState from './NoAssignmentsState';
import NoLicenseState from './NoLicenseState';

const BASE = 'PAGES.ASSIGNMENTS.TEACHER.SECTION';

function AssignmentsBody({ assignments }: { assignments: TeacherAssignment[] }) {
  const { assignmentsEndIn24hours, assignmentsEndIn7Days, assignmentsEndOver7Days } =
    splitAssignmentsByEndDateCategory(assignments);

  return (
    <>
      <AssignmentDivision endDateCategory="TODAY" assignments={assignmentsEndIn24hours} />
      <AssignmentDivision endDateCategory="THIS_WEEK" assignments={assignmentsEndIn7Days} />
      <AssignmentDivision endDateCategory="LATER" assignments={assignmentsEndOver7Days} />
    </>
  );
}

function ArchivedAssignmentsBody({ assignments }: { assignments: TeacherAssignment[] }) {
  const {
    i18n: { language },
  } = useTranslation();

  const { endedThisWeek, endedLastWeek, endedByMonth } = splitAssignmentsByPastEndDateCategory(assignments, language);

  return (
    <>
      <AssignmentDivision endDateCategory="LAST_WEEK" assignments={endedThisWeek} />
      <AssignmentDivision endDateCategory="LAST_TWO_WEEKS" assignments={endedLastWeek} />
      {Object.entries(endedByMonth).map(([key, value]) => (
        <AssignmentDivision key={key} endDateCategory="IN_LAST_MONTH" assignments={value} monthAndYear={key} />
      ))}
    </>
  );
}

type AssignmentsTableProps = {
  assignments: TeacherAssignment[];
  archived?: boolean;
};

function AssignmentsTable({ assignments, archived }: AssignmentsTableProps) {
  const { t } = useTranslation();

  return (
    <div className="scroll-table-container">
      <table className="assignments-table" data-testid="assignments-table">
        <thead>
          <tr>
            <Cell.HeaderText title={t(`${BASE}.TABLE.HEADER.MATERIAL`)} sticky minWidth="xl" maxWidth="xl" />
            <Cell.HeaderText title={t(`${BASE}.TABLE.HEADER.SECTION`)} sticky minWidth="md" maxWidth="md" />
            <Cell.HeaderText title={t(`${BASE}.TABLE.HEADER.PERIOD`)} sticky minWidth={archived ? 'sm' : 'md'} />
            <Cell.HeaderText title={t(`${BASE}.TABLE.HEADER.GROUP`)} sticky minWidth="md" maxWidth="md" />
            <Cell.HeaderText title={t(`${BASE}.TABLE.HEADER.STUDENTS`)} sticky minWidth="xs" />
            <Cell.HeaderEmpty className="actions-cell" sticky />
          </tr>
        </thead>
        <tbody>
          {archived ? (
            <ArchivedAssignmentsBody assignments={assignments} />
          ) : (
            <AssignmentsBody assignments={assignments} />
          )}
        </tbody>
      </table>
    </div>
  );
}

interface Props {
  isLoadingGroups: boolean;
  groups: Group[];
  archived?: boolean;
}

function Assignments({ isLoadingGroups, groups }: Omit<Props, 'archived'>) {
  const { t } = useTranslation();
  const { module } = useContext(ModuleContext);
  const { assignments, isLoading: isLoadingAssignments, archived } = useContext(TeacherAssignmentsContext);

  const hasDigitalManualLicense = module?.licenses.hasDigitalManualLicenseForTeacher;
  const isLoading = isLoadingGroups || isLoadingAssignments;

  let content;

  if (isLoading) {
    content = <LoadingState variant="table" aria-label={t(`${BASE}.TABLE.EMPTY_STATE.A11Y_LABEL`)} />;
  } else if (!isLoadingGroups && groups.length === 0) {
    content = <GroupsEmptyState />;
  } else if (!hasDigitalManualLicense) {
    content = <NoLicenseState />;
  } else if (!isLoadingAssignments && assignments.length === 0) {
    content = <NoAssignmentsState archived={archived} />;
  } else {
    content = <AssignmentsTable assignments={assignments} archived={archived} />;
  }

  return (
    <Section
      title={t(`${BASE}.${archived ? 'TITLE_ARCHIVED' : 'TITLE'}`)}
      className="teacher-assignments"
      contentClassName="content-wrapper"
    >
      {content}
    </Section>
  );
}

function WrappedAssignments({ archived, ...props }: Props) {
  return (
    <TeacherAssignmentsContextProvider archived={archived ?? false}>
      <Assignments {...props} />
    </TeacherAssignmentsContextProvider>
  );
}

export default WrappedAssignments;
